import { deriveCouponStatus, type Coupon } from './coupons.js';

/**
 * Promo codes on orders (MYP-95).
 *
 * A code can be checked properly only once an order has a billing weight: `OrderCodenoCheck`
 * needs one, and it exists only after the warehouse packs the order (missing-APIs B3). So there
 * are two checks, one at each end of an order's life:
 *
 * - **Creating an order.** No weight yet. The code is checked against the customer's own voucher
 *   list (`get_onlinecode`) and sent with `orders_create` as `codeno`. The vendor applies the
 *   discount when it prices the order: live, 33 orders on the test account carry a code, 13 of
 *   them still unpriced, and priced ones show a negative "Discount" fee line.
 * - **Paying an order.** It has a billing weight now, so `OrderCodenoCheck` runs for real and its
 *   discount is sent with `orders_pay`. Only for an order that has no code yet: a code applies once.
 */

/** Why a code the customer typed is refused before anything is sent. */
export type PromoRejection =
  /** On the customer's list, but past its end date. */
  | 'expired'
  /** On the customer's list, and the vendor has marked it spent. */
  | 'used'
  /** On the customer's list, but its start date has not come yet. */
  | 'notYetValid'
  /** An air-freight voucher on a sea channel, or the other way round. */
  | 'wrongTransport'
  /** A shopping-mall voucher, which does not apply to shipping at all. */
  | 'notForShipping';

export type PromoAssessment =
  /** Nothing typed. */
  | { readonly kind: 'empty' }
  /** Refused before sending. `coupon` is the customer's voucher the code matched. */
  | { readonly kind: 'rejected'; readonly reason: PromoRejection; readonly coupon: Coupon }
  /** A usable voucher from the customer's own list. */
  | { readonly kind: 'voucher'; readonly code: string; readonly coupon: Coupon }
  /**
   * Not on the customer's list. Public promo codes (live: `MYPOZ11`) never are, so this is not a
   * refusal: the vendor checks it when the order is created and priced (decision (a), MYP-95).
   */
  | { readonly kind: 'unlisted'; readonly code: string };

/**
 * Which transport a voucher type is for, read from the vendor's type codes (live, ten of them):
 * `AIRFEE*` air, `SEAFEE*` sea, `SHOP*` the shopping mall. Anything else (service-fee vouchers,
 * sign-up and distributed coupons) is not tied to a transport, and the vendor decides at pricing.
 */
function voucherScope(typeCode: string): 'AIR' | 'SEA' | 'SHOP' | 'ANY' {
  const code = typeCode.toUpperCase();
  if (code.startsWith('AIRFEE')) return 'AIR';
  if (code.startsWith('SEAFEE')) return 'SEA';
  if (code.startsWith('SHOP')) return 'SHOP';
  return 'ANY';
}

/**
 * Checks a typed code against the customer's vouchers and the chosen channel's transport, before
 * a new order is created. Codes are matched exactly (after trimming): the vendor's codes are
 * mixed-case (`TYA5wHGg`), so folding case could match a different code.
 *
 * When the list holds the same code more than once, a usable copy wins.
 */
export function assessPromoCode(
  typed: string,
  vouchers: readonly Coupon[],
  transportType: string | undefined,
  now: Date,
): PromoAssessment {
  const code = typed.trim();
  if (code === '') return { kind: 'empty' };

  const matches = vouchers.filter((voucher) => voucher.code === code);
  if (matches.length === 0) return { kind: 'unlisted', code };

  const coupon =
    matches.find((voucher) => deriveCouponStatus(voucher, now) === 'valid') ?? matches[0]!;

  const status = deriveCouponStatus(coupon, now);
  if (status === 'used') return { kind: 'rejected', reason: 'used', coupon };
  if (status === 'expired') return { kind: 'rejected', reason: 'expired', coupon };
  if (status === 'scheduled') return { kind: 'rejected', reason: 'notYetValid', coupon };

  const scope = voucherScope(coupon.typeCode);
  if (scope === 'SHOP') return { kind: 'rejected', reason: 'notForShipping', coupon };
  const transport = transportType?.toUpperCase();
  if ((scope === 'AIR' || scope === 'SEA') && transport !== undefined && transport !== scope) {
    return { kind: 'rejected', reason: 'wrongTransport', coupon };
  }

  return { kind: 'voucher', code, coupon };
}

/** The transport a voucher is limited to, for the "air orders only" line. Undefined if none. */
export function voucherTransport(coupon: Coupon): 'AIR' | 'SEA' | undefined {
  const scope = voucherScope(coupon.typeCode);
  return scope === 'AIR' || scope === 'SEA' ? scope : undefined;
}

/**
 * The discount in `OrderCodenoCheck`'s reply, in integer cents.
 *
 * Live, `codefee` sits at the top level (`{code, codefee, msg}`); the document puts it inside
 * `info`. Both are read, top level first. A missing or non-numeric value is undefined, never 0: a
 * "valid" code with no readable discount must not be sent as a zero discount.
 */
export function readPromoDiscountCents(reply: unknown): number | undefined {
  if (typeof reply !== 'object' || reply === null) return undefined;
  const body = reply as Readonly<Record<string, unknown>>;
  const info = body['info'];
  const raw =
    body['codefee'] ??
    (typeof info === 'object' && info !== null
      ? (info as Readonly<Record<string, unknown>>)['codefee']
      : undefined);

  const value = typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw.trim()) : NaN;
  if (!Number.isFinite(value) || value < 0) return undefined;
  return Math.round(value * 100);
}
